import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../api/client'
import type { TranscriptVersion } from '../api/types'
import { episodeKey, refreshEpisode } from './useShows'

const transcriptKey = (ws: string, id: string) => [...episodeKey(ws, id), 'transcript'] as const

export function useTranscript(ws: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: transcriptKey(ws, id),
    queryFn: () => api<TranscriptVersion>(`/workspaces/${ws}/episodes/${id}/transcript`),
    enabled,
  })
}

/** Save an edit. The reply is the new version, which replaces the cached one. */
function useTranscriptEdit<Input>(ws: string, id: string, request: (input: Input) => Promise<TranscriptVersion>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (saved) => queryClient.setQueryData(transcriptKey(ws, id), saved),
  })
}

export function useEditSegment(ws: string, id: string) {
  return useTranscriptEdit(ws, id, (input: { segmentId: string; text: string; baseVersion: number }) =>
    api<TranscriptVersion>(`/workspaces/${ws}/episodes/${id}/transcript/segments/${encodeURIComponent(input.segmentId)}`, {
      method: 'PATCH',
      body: json({ text: input.text, base_version: input.baseVersion }),
    }),
  )
}

export function useRenameSpeaker(ws: string, id: string) {
  return useTranscriptEdit(ws, id, (input: { from: string; to: string; baseVersion: number }) =>
    api<TranscriptVersion>(`/workspaces/${ws}/episodes/${id}/transcript/speakers`, {
      method: 'POST',
      body: json({ from: input.from, to: input.to, base_version: input.baseVersion }),
    }),
  )
}

export function useTranscribe(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ pipeline_id: string }>(`/workspaces/${ws}/episodes/${id}/transcribe`, { method: 'POST' }),
    onSuccess: () => refreshEpisode(queryClient, ws, id),
  })
}

/** Download link for the latest transcript's captions. */
export function captionsUrl(ws: string, id: string, format: 'vtt' | 'srt'): string {
  return `/api/v1/workspaces/${ws}/episodes/${id}/transcript/captions/${format}`
}
